import React, { useEffect, useState } from 'react'

import { Link, Navigate, useParams } from 'react-router-dom'

import './InvestorProfile.scss'

import iconBack from '../../../../../Assets/Icon/AdminInvestors/mna1u69h-kz1jzr2.svg'
import iconChevron from '../../../../../Assets/Icon/AdminInvestors/mna1u69h-2glfga7.svg'
import iconKycApproved from '../../../../../Assets/Icon/AdminInvestors/mna1u69k-jq23c8t.svg'

import customerService from 'Services/customerService'
import { CustomerType } from 'Models/customerType'
import investorTokenAssetsService, {
  type InvestorTokenAssetInvestorSummary,
} from 'Services/investorTokenAssetsService'
import InvestmentsList from './components/InvestmentsList'
import TransactionsList from './components/TransactionsList'
import DocumentsList from './components/DocumentsList'
import ActivityLogList from './components/ActivityLogList'
import { CurrencyFormatter } from 'Helpers'

type InvestorFilter = 'all' | 'active' | 'suspended'

const InvestorProfile: React.FC = () => {
  const { filter, investorId } = useParams()
  const activeFilter = filter as InvestorFilter
  const isValidFilter =
    activeFilter === 'all' || activeFilter === 'active' || activeFilter === 'suspended'
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [investor, setInvestor] = useState<CustomerType | null>(null)
  const [investmentSummary, setInvestmentSummary] =
    useState<InvestorTokenAssetInvestorSummary | null>(null)
  const [activeTab, setActiveTab] = useState<
    'investments' | 'transactions' | 'documents' | 'activity'
  >('investments')

  const getErrorMessage = (e: unknown) => {
    if (typeof e === 'object' && e !== null && 'message' in e) {
      const message = (e as { message?: unknown }).message
      if (typeof message === 'string') return message
      if (message != null) return String(message)
    }
    return null
  }

  useEffect(() => {
    if (!isValidFilter || !investorId) return

    const fetchInvestor = async () => {
      setLoading(true)
      setErrorMessage(null)
      try {
        const response = await customerService.getDetails(investorId)
        const data = response?.data?.data
        const dataset = (data?.dataset ?? []) as CustomerType[]
        const first = dataset[0] ?? null
        setInvestor(first)
      } catch (e) {
        setInvestor(null)
        setErrorMessage(getErrorMessage(e) ?? 'Failed to load investor details')
      } finally {
        setLoading(false)
      }
    }

    fetchInvestor()
  }, [investorId, isValidFilter])

  useEffect(() => {
    if (!isValidFilter || !investorId) return

    const fetchInvestmentSummary = async () => {
      try {
        const response = await investorTokenAssetsService.investorSummary(investorId)
        setInvestmentSummary(
          (response?.data?.data ?? null) as InvestorTokenAssetInvestorSummary | null,
        )
      } catch {
        setInvestmentSummary(null)
      }
    }

    fetchInvestmentSummary()
  }, [investorId, isValidFilter])

  if (!isValidFilter || !investorId) return <Navigate to='/blockchain/investors/all' replace />

  const fullName = investor ? `${investor.firstName ?? ''} ${investor.lastName ?? ''}`.trim() : ''
  const initials =
    investor && (investor.firstName || investor.lastName)
      ? `${(investor.firstName ?? '').slice(0, 1)}${(investor.lastName ?? '').slice(0, 1)}`.toUpperCase()
      : '—'
  const accountStatus = investor?.isEnabled ? 'Active' : 'Suspended'
  const kycStatus = investor?.investorKycStatus ?? '-'

  const walletBalance =
    investor?.walletBalance?.amountMajor != null && investor?.walletBalance?.currency
      ? `${investor.walletBalance.currency} ${investor.walletBalance.amountMajor.toLocaleString()}`
      : '-'
  const loanBalance =
    investor?.loanBalance?.amountMajor != null && investor?.loanBalance?.currency
      ? `${investor.loanBalance.currency} ${investor.loanBalance.amountMajor.toLocaleString()}`
      : '-'
  const resolvedAccountId = investor?.accountId ?? investorId

  return (
    <div className='admin-investor'>
      <div className='admin-investor__breadcrumb'>
        <Link to={`/blockchain/investors/${activeFilter}`} className='admin-investor__crumb'>
          <img src={iconBack} alt='' className='admin-investor__crumb-icon' />
          <span>Investors Management</span>
        </Link>
        <img src={iconChevron} alt='' className='admin-investor__crumb-sep' />
        <span className='admin-investor__crumb-current'>Investor Profile</span>
      </div>

      <div className='admin-investor__top'>
        <div className='admin-investor__name'>
          <span>{fullName || investor?.accountId || '-'}</span>
          <span
            className={[
              'admin-investor__pill',
              accountStatus === 'Active'
                ? 'admin-investor__pill--active'
                : 'admin-investor__pill--suspended',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {accountStatus}
          </span>
        </div>

        {/* <div className='admin-investor__actions'>
          <button type='button' className='admin-investor__add'>
            + Add Investment
          </button>
          <button type='button' className='admin-investor__more' aria-label='More'>
            <span />
            <span />
            <span />
          </button>
        </div> */}
      </div>

      <div className='admin-investor__section-title'>Personal Information</div>

      <div className='admin-investor-card'>
        <div className='admin-investor-card__avatar'>{initials}</div>

        <div className='admin-investor-card__grid'>
          <div className='admin-investor-card__field'>
            <div className='admin-investor-card__label'>Full Name</div>
            <div className='admin-investor-card__value'>{fullName || '-'}</div>
          </div>
          <div className='admin-investor-card__field'>
            <div className='admin-investor-card__label'>Investor ID</div>
            <div className='admin-investor-card__value'>{investor?.accountId ?? '-'}</div>
          </div>
          <div className='admin-investor-card__field'>
            <div className='admin-investor-card__label'>Email Address</div>
            <div className='admin-investor-card__value'>{investor?.emailAddress ?? '-'}</div>
          </div>

          <div className='admin-investor-card__field'>
            <div className='admin-investor-card__label'>Phone Number</div>
            <div className='admin-investor-card__value'>{investor?.msisdn ?? '-'}</div>
          </div>
          <div className='admin-investor-card__field'>
            <div className='admin-investor-card__label'>Nationality</div>
            <div className='admin-investor-card__value'>{investor?.country ?? '-'}</div>
          </div>
          <div className='admin-investor-card__field'>
            <div className='admin-investor-card__label'>KYC Status</div>
            <div className='admin-investor-card__value admin-investor-card__value--inline'>
              <img src={iconKycApproved} alt='' className='admin-investor-card__kyc-icon' />
              <span>{kycStatus}</span>
            </div>
          </div>
        </div>
      </div>

      <div className='row g-3 g-xl-4 mt-3'>
        <div className='col-12 col-md-6 col-xl-3'>
          <div className='admin-investor-kpi admin-investor-kpi--blue'>
            <div className='admin-investor-kpi__label'>Total Invested</div>
            <div className='admin-investor-kpi__value'>
              {investmentSummary
                ? `₦${investmentSummary.totalPrincipalInvestedNairaMajor.toLocaleString()}`
                : '-'}
            </div>
          </div>
        </div>
        <div className='col-12 col-md-6 col-xl-3'>
          <div className='admin-investor-kpi admin-investor-kpi--green'>
            <div className='admin-investor-kpi__label'>Active Investments</div>
            <div className='admin-investor-kpi__value'>
              ₦{CurrencyFormatter(investmentSummary?.totalActivePrincipalInvestedNairaMajor ?? 0)}
            </div>
          </div>
        </div>
        <div className='col-12 col-md-6 col-xl-3'>
          <div className='admin-investor-kpi admin-investor-kpi--purple'>
            <div className='admin-investor-kpi__label'>Total Token Owned</div>
            <div className='admin-investor-kpi__value'>
              {' '}
              {investmentSummary ? investmentSummary.totalTokensOwned : '-'}
            </div>
          </div>
        </div>
        <div className='col-12 col-md-6 col-xl-3'>
          <div className='admin-investor-kpi admin-investor-kpi--orange'>
            <div className='admin-investor-kpi__label'>Wallet Balance</div>
            <div className='admin-investor-kpi__value'>{walletBalance}</div>
          </div>
        </div>
      </div>

      {(loading || errorMessage) && (
        <div className='mt-3'>
          {loading ? <div>Loading investor...</div> : null}
          {!loading && errorMessage ? <div>{errorMessage}</div> : null}
          {!loading && !errorMessage && !investor ? <div>Investor not found</div> : null}
        </div>
      )}

      <div className='admin-investor__tabs mt-3'>
        <button
          type='button'
          className={[
            'admin-investor__tab',
            activeTab === 'investments' ? 'admin-investor__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setActiveTab('investments')}
        >
          Investments
        </button>
        <button
          type='button'
          className={[
            'admin-investor__tab',
            activeTab === 'transactions' ? 'admin-investor__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setActiveTab('transactions')}
        >
          Transactions
        </button>
        <button
          type='button'
          className={[
            'admin-investor__tab',
            activeTab === 'documents' ? 'admin-investor__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setActiveTab('documents')}
        >
          Documents
        </button>
        <button
          type='button'
          className={[
            'admin-investor__tab',
            activeTab === 'activity' ? 'admin-investor__tab--active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => setActiveTab('activity')}
        >
          Activity Log
        </button>
      </div>

      {activeTab === 'investments' ? <InvestmentsList accountId={resolvedAccountId} /> : null}
      {activeTab === 'transactions' ? (
        <TransactionsList customerAccountId={resolvedAccountId} customerMsisdn={investor?.msisdn} />
      ) : null}
      {activeTab === 'documents' ? <DocumentsList accountId={resolvedAccountId} /> : null}
      {activeTab === 'activity' ? <ActivityLogList accountId={resolvedAccountId} /> : null}
    </div>
  )
}

export default InvestorProfile
